/**
 * BasisRetrieval.tsx — WP31: why a decision was made, retrieved fresh.
 *
 * THE REQUIREMENT THIS SATISFIES, precisely
 * ----------------------------------------
 * A fresh browser context must be able to retrieve the selected alternative,
 * the rejected alternatives, the assumptions, the reopening triggers, the
 * selection basis, and whether that record is draft or published.
 *
 * THE PROHIBITION, and how it is honoured structurally
 * ---------------------------------------------------
 * Not one word of that basis may come from chat state, from model memory, or
 * from a fixture. Those three are the failure modes the requirement exists to
 * rule out, and they are ruled out here by construction rather than by promise:
 *
 *   • This component holds NO module-level cache. It fetches on mount, from the
 *     client, every time. A second mount in the same session re-requests, so a
 *     "cached" basis can never be shown as a retrieved one.
 *   • It reads NOTHING but the Engine's response. There is no fallback text, no
 *     default narrative, and no local vocabulary of what a rationale is made
 *     of. If the Engine returns nothing, the panel says the Engine returned
 *     nothing — which is the honest state, and is itself the retrieval result.
 *   • It never composes an explanation. Every word on screen is either the
 *     Engine's, or a fixed statement about what the browser could not retrieve.
 *
 * WHY NO VOCABULARY APPEARS IN `src/`
 * ----------------------------------
 * There is no list, here or anywhere in this repository, of the parts a basis is
 * made of. The Engine declares which result properties are PRIMARY, and this
 * component highlights exactly those, in the Engine's order, and renders the rest
 * generically.
 *
 * That is not a stylistic choice. A hand-written list of a rationale's parts
 * would be a second source of truth for engineering meaning: it would name the
 * parts before the Engine published them, and it would go on naming arms that
 * no longer exist. So a rationale capability the Engine has not yet published is
 * rendered as a specific, named absence rather than an empty screen — the
 * browser cannot invent the vocabulary, and inventing it is the defect this
 * whole workbench exists to avoid.
 */

import { useMemo } from "react";
import { useClient } from "../../api/ClientContext";
import { buildReadRequest, inspect, type ActionPlan } from "../../api/capabilityAction";
import type { CapabilityDescriptor, CapabilityManifest } from "../../api/capabilityManifest";
import { ErrorBanner } from "../../components/ErrorBanner";
import { Empty, Panel } from "../../components/Panel";
import { useAsync } from "../../stores/useAsync";
import { ValueTree } from "../capabilities/ValueTree";

/** Whether a basis is a draft or has been published. Read from the Engine. */
export interface BasisState {
  /** The Engine's own record state, verbatim. Never inferred here. */
  readonly recordState: string | undefined;
  /** The canonical revision the record was read at, when the Engine sent one. */
  readonly canonicalRevision: string | undefined;
  /** Whether the Engine said the record is published, or the state is unknown. */
  readonly publication: "published" | "draft" | "unreported";
}

function schemaPropertyType(schema: unknown): string | undefined {
  return typeof schema === "object" && schema !== null && typeof (schema as { type?: unknown }).type === "string"
    ? (schema as { type: string }).type
    : undefined;
}

function schemaEnum(schema: unknown): readonly string[] {
  if (typeof schema !== "object" || schema === null) return [];
  const options = (schema as { enum?: unknown }).enum;
  if (!Array.isArray(options)) return [];
  return options.filter((option): option is string => typeof option === "string");
}

/**
 * Find the record state in an Engine result, by the Engine's own declaration.
 *
 * A result's state is whatever the Engine called it, and this function reports
 * that value. Whether it ASSERTS PUBLICATION is decided by comparing the value
 * against the publication tokens the descriptor's own schema enumerates — never
 * against a list written here, because a hand-written list of an Engine's state
 * machine is a second source of truth for engineering meaning.
 *
 * When the Engine publishes no closed vocabulary for the state, the answer is
 * `unreported`, which is the safe direction: an unretrieved record must never be
 * shown as a published one.
 */
export function readBasisState(descriptor: CapabilityDescriptor, raw: unknown): BasisState {
  const record = typeof raw === "object" && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
  const properties = (descriptor.resultSchema?.properties ?? {}) as Record<string, unknown>;

  // Publication is claimed only where the Engine's schema names it. `null` means
  // the Engine published no vocabulary, which is NOT the same as "not published".
  const published: string[] = [];
  for (const schema of Object.values(properties)) {
    for (const option of schemaEnum(schema)) {
      if (/publish/i.test(option) && !published.includes(option)) published.push(option);
    }
  }
  const publicationVocabulary: ReadonlySet<string> | null = published.length > 0 ? new Set(published) : null;

  // The state property is the first string-valued property with a CLOSED
  // enumeration: a bounded vocabulary is a state machine, an open one is a note.
  const stateField = Object.keys(properties).find(name => {
    const schema = properties[name];
    return schemaPropertyType(schema) === "string" && schemaEnum(schema).length > 0;
  });

  const recordState = stateField !== undefined && typeof record[stateField] === "string"
    ? (record[stateField] as string)
    : undefined;
  const canonicalRevision = typeof record.canonicalRevision === "string" ? record.canonicalRevision : undefined;
  if (recordState === undefined) return { recordState: undefined, canonicalRevision, publication: "unreported" };
  // THE THREE-WAY OUTCOME, and the branch that was wrong.
  //
  // `published` is claimed only where the Engine published a vocabulary AND the
  // reported value is in it. `draft` is claimed only where the Engine published a
  // vocabulary and the value is NOT in it — because knowing a record is not
  // published requires knowing what published looks like.
  //
  // Where the Engine published NO publication vocabulary at all, the answer is
  // `unreported`, never `draft`. The previous version returned `draft` in that
  // case, so every query descriptor on the integrated Engine — whose result
  // schemas publish a terminal `status` and no publication vocabulary — rendered
  // "Draft — not yet canonical" for values like `insufficient_data`. That is a
  // false claim about a real engineering record, made by a browser that had been
  // told nothing about publication at all, and it contradicted the contract
  // written directly above it in this file.
  if (publicationVocabulary === null) {
    return { recordState, canonicalRevision, publication: "unreported" };
  }
  return {
    recordState,
    canonicalRevision,
    publication: publicationVocabulary.has(recordState) ? "published" : "draft",
  };
}

export function BasisPanel({ manifest, descriptor, projectId, changeSetTitle }: {
  manifest: CapabilityManifest;
  descriptor: CapabilityDescriptor;
  projectId: string;
  changeSetTitle?: string;
}): JSX.Element {
  const client = useClient();
  const resolution = useMemo(() => inspect(manifest, descriptor), [descriptor, manifest]);
  const readable = resolution.addressable && resolution.plan.phases.includes("read");
  const plan: ActionPlan | undefined = resolution.addressable ? resolution.plan : undefined;

  // NO CACHE. The key is the mount, so every fresh context re-requests. A test
  // that renders this twice and sees one request is showing a bug.
  const retrieved = useAsync(
    async () => {
      if (!plan) return undefined;
      return client.capabilityRead(projectId, buildReadRequest(plan, {}).body);
    },
    [client, plan, projectId],
    { enabled: readable },
  );

  const state = useMemo(() => readBasisState(descriptor, retrieved.data), [descriptor, retrieved.data]);

  return (
    <Panel title="Selection basis">
      {!resolution.addressable ? (
        <Empty>
          This capability cannot be read from this browser. {resolution.unaddressable.unavailable.detail}
        </Empty>
      ) : !readable ? (
        <Empty>
          <code>{descriptor.id}</code> is not a read. It mutates through the change set, so it has no basis to
          retrieve until it has been applied and the Engine has recorded why.
        </Empty>
      ) : retrieved.loading ? (
        <Empty>Retrieving from the Engine…</Empty>
      ) : retrieved.data === undefined ? (
        <Empty>The Engine returned no basis for this capability.</Empty>
      ) : (
        <>
          <dl className="facts compact">
            <dt>Capability</dt><dd><code>{descriptor.id}</code></dd>
            <dt>Record state</dt>
            <dd>
              {/* An unreported state is rendered as unreported. A draft banner
                  for a record the Engine never described would be a claim about
                  a real engineering record that nobody made. */}
              {state.publication === "unreported"
                ? <span className="bad">The Engine did not report a record state.</span>
                : state.recordState}
            </dd>
            <dt>Publication</dt>
            <dd>
              {state.publication === "published" ? "Published"
                : state.publication === "draft" ? "Draft — not yet canonical"
                : "Unreported"}
            </dd>
            {state.canonicalRevision !== undefined && (
              <><dt>Canonical revision</dt><dd className="request-id">{state.canonicalRevision}</dd></>
            )}
            {changeSetTitle !== undefined && <><dt>Working in</dt><dd>{changeSetTitle}</dd></>}
          </dl>
          {state.publication === "unreported" && (
            <p className="muted">
              The Engine published no closed state vocabulary for this result, so this browser will not say whether
              the record is draft or published. It shows what the Engine returned, unlabelled.
            </p>
          )}
          <h3>What the Engine returned</h3>
          <ValueTree
            value={retrieved.data}
            schema={descriptor.resultSchema}
            highlight={descriptor.presentation.primaryFields}
          />
        </>
      )}
      <ErrorBanner error={retrieved.error} onDismiss={retrieved.reload} />
      <p className="muted">
        Retrieved from the Engine on every open. Nothing here is remembered between visits, and nothing here was
        written by this browser.
      </p>
    </Panel>
  );
}
